
import { defineComponent, ref, reactive, onMounted, watch, computed } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { ReloadOutlined } from '@ant-design/icons-vue';
import { OioButton, OioForm, OioFormItem, OioInput, OioTag, OioModal, OioPagination } from '@kunlun/vue-ui-antd';
import FileUpload from '../../UploadImage/field/common/File.vue';
import ImportInvoiceInfoDialog from './ImportInvoiceInfoDialog.vue';
import { DeliInvoiceInfo, PageQueryParams } from '../types';
import { queryInvoiceInfoList, querySellerInvoiceInfoList, createInvoiceInfo, updateInvoiceInfo, approveInvoiceInfo, rejectInvoiceInfo, batchApproveInvoiceInfo, deleteInvoiceInfo, batchDeleteInvoiceInfo, batchExportInvoiceInfo } from '../api/billingBill';
import { approvalStatusTextMap, approvalStatusColorMap } from '../constants';
import { usePortalType } from '../hooks/usePortalType';

export default defineComponent({
  name: 'InvoiceInfoManagePage',
  components: { ReloadOutlined, OioButton, OioForm, OioFormItem, OioInput, OioTag, OioModal, OioPagination, FileUpload, ImportInvoiceInfoDialog },
  setup() {
    const loading = ref(false);
    const submitLoading = ref(false);
    const exportLoading = ref(false);
    const tableData = ref<DeliInvoiceInfo[]>([]);
    const showAddForm = ref(false);
    const editingRecord = ref<DeliInvoiceInfo | null>(null);
    const tableRef = ref();
    const rejectVisible = ref(false);
    const rejectLoading = ref(false);
    const rejectReason = ref('');
    const rejectingRecord = ref<DeliInvoiceInfo | null>(null);
    const importVisible = ref(false);
    const { isDistributor, isSeller, isOperationCenter } = usePortalType();

    const searchForm = reactive({
      code: '',
      distributorCode: '',
      distributorErpCode: '',
      approvalStatus: undefined as string | undefined
    });

    const getEmptyFormState = () => ({
      code: '',
      distributorCode: '',
      distributorErpCode: '',
      salesOrgCode: '',
      invoiceTitle: '',
      taxNo: '',
      bankName: '',
      bankAccount: '',
      phone: '',
      address: '',
      deliveryAddress: '',
      businessLicenseFile: '',
      remark: ''
    });

    const formState = reactive(getEmptyFormState());

    const pagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0
    });

    /** 重置表单为初始状态 */
    const resetForm = () => {
      Object.assign(formState, getEmptyFormState());
    };


    /** 营业执照回填值：字符串 url 转 FileUpload 可识别的文件对象数组 */
    const licenseFileValue = computed(() =>
      formState.businessLicenseFile ? [{ url: formState.businessLicenseFile, name: '营业执照' }] : []
    );

    // 监听 licenseFileValue 变空时同步清空 formState，防止删除文件后校验失效
    watch(licenseFileValue, (val) => {
      if (!val || val.length === 0) {
        formState.businessLicenseFile = '';
      }
    });

    const buildRsql = (): string => {
      const conditions: string[] = ['(1==1)'];
      if (searchForm.code) conditions.push(`code=like='%${searchForm.code}%'`);
      if (searchForm.distributorCode) conditions.push(`distributorCode=='${searchForm.distributorCode}'`);
      if (searchForm.distributorErpCode) conditions.push(`distributorErpCode=='${searchForm.distributorErpCode}'`);
      if (searchForm.approvalStatus) conditions.push(`approvalStatus=='${searchForm.approvalStatus}'`);
      return conditions.join(' and ');
    };

    const fetchData = async () => {
      loading.value = true;
      try {
        const params: PageQueryParams = {
          currentPage: pagination.current,
          size: pagination.pageSize,
          rsql: buildRsql()
        };
        // 品牌端使用省区过滤后的查询接口
        const result = isSeller.value
          ? await querySellerInvoiceInfoList(params)
          : await queryInvoiceInfoList(params);
        tableData.value = result.content || [];
        pagination.total = result.totalElements || 0;
      } catch (e: any) {
        message.error(e?.message || '查询失败');
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.current = 1;
      fetchData();
    };

    const handleReset = () => {
      searchForm.code = '';
      searchForm.distributorCode = '';
      searchForm.distributorErpCode = '';
      searchForm.approvalStatus = undefined;
      pagination.current = 1;
      fetchData();
    };

    const handlePaginationChange = (page: number, pageSize: number) => {
      pagination.current = page;
      pagination.pageSize = pageSize;
      fetchData();
    };

    /** 打开新增对话框 —— 先重置表单和编辑状态 */
    const handleAdd = () => {
      editingRecord.value = null;
      resetForm();
      showAddForm.value = true;
    };

    const handleEdit = (record: DeliInvoiceInfo) => {
      editingRecord.value = record;
      Object.assign(formState, {
        code: record.code,
        distributorCode: record.distributorCode,
        distributorErpCode: record.distributorErpCode,
        salesOrgCode: record.salesOrgCode,
        invoiceTitle: record.invoiceTitle,
        taxNo: record.taxNo,
        bankName: record.bankName,
        bankAccount: record.bankAccount,
        phone: record.phone,
        address: record.address,
        deliveryAddress: record.deliveryAddress,
        businessLicenseFile: record.businessLicenseFile || '',
        remark: record.remark || ''
      });
      showAddForm.value = true;
    };

    /** 关闭对话框 —— 返回 true 让框架关闭弹窗 */
    const handleCancel = () => {
      return true;
    };

    // 对话框关闭后自动重置表单（覆盖 X 按钮、遮罩点击等关闭场景）
    watch(showAddForm, (val) => {
      if (!val) {
        editingRecord.value = null;
        resetForm();
      }
    });

    const handleSubmit = async (): Promise<boolean> => {
      // 逐字段校验必填项：经销商编码、ERP客户编码、销售组织编码、发票抬头、纳税人识别号
      // （营业执照附件另行校验；其余字段选填）
      const requiredFields: Array<{ key: keyof typeof formState; label: string }> = [
        { key: 'distributorCode', label: '经销商编码' },
        { key: 'distributorErpCode', label: 'ERP客户编码' },
        { key: 'salesOrgCode', label: '销售组织编码' },
        { key: 'invoiceTitle', label: '发票抬头' },
        { key: 'taxNo', label: '纳税人识别号' }
      ];
      for (const field of requiredFields) {
        const val = formState[field.key];
        if (typeof val === 'string' && !val.trim()) {
          message.warning(`请填写${field.label}`);
          return false;
        }
      }
      // 营业执照必传（新增和编辑均校验）
      if (!formState.businessLicenseFile) {
        message.warning('营业执照附件必传');
        return false;
      }
      submitLoading.value = true;
      try {
        const submitData: Record<string, any> = { ...formState };
        // 编码由后端 @Model.Code 自动生成，前端不提交（新建生成、编辑不可改）
        delete submitData.code;
        if (editingRecord.value) {
          await updateInvoiceInfo(editingRecord.value.id, submitData);
          message.success('更新成功');
        } else {
          await createInvoiceInfo(submitData);
          message.success('创建成功');
        }
        fetchData();
        return true;
      } catch (e: any) {
        message.error(e?.message || '提交失败');
        return false;
      } finally {
        submitLoading.value = false;
      }
    };

    const handleApprove = async (record: DeliInvoiceInfo) => {
      Modal.confirm({
        title: '审批确认',
        content: `确定审批通过开票信息 ${record.code} 吗？`,
        onOk: async () => {
          try {
            await approveInvoiceInfo(record.id);
            message.success('审批通过');
            fetchData();
          } catch (e: any) {
            message.error(e?.message || '审批失败');
          }
        }
      });
    };

    /** 打开驳回原因弹窗 */
    const handleReject = (record: DeliInvoiceInfo) => {
      rejectingRecord.value = record;
      rejectReason.value = '';
      rejectVisible.value = true;
    };

    /** 确认驳回，提交审批人输入的原因 */
    const handleRejectConfirm = async (): Promise<boolean> => {
      if (!rejectReason.value.trim()) {
        message.warning('请输入驳回原因');
        return false;
      }
      if (!rejectingRecord.value) return false;
      rejectLoading.value = true;
      try {
        await rejectInvoiceInfo(rejectingRecord.value.id, rejectReason.value.trim());
        message.success('已驳回');
        fetchData();
        return true;
      } catch (e: any) {
        message.error(e?.message || '驳回失败');
        return false;
      } finally {
        rejectLoading.value = false;
      }
    };

    /** 驳回弹窗取消 */
    const handleRejectCancel = () => {
      return true;
    };

    /** 获取表格勾选行（运营中心批量操作） */
    const getCheckedRows = (): DeliInvoiceInfo[] => {
      return tableRef.value?.getCheckboxRecords?.() || [];
    };

    /** 批量审核：仅审核待审批状态的勾选行 */
    const handleBatchApprove = () => {
      const rows = getCheckedRows();
      if (!rows.length) {
        message.warning('请先勾选需要审核的开票信息');
        return;
      }
      const pendingRows = rows.filter((r: DeliInvoiceInfo) => r.approvalStatus === 'PENDING_APPROVAL');
      if (!pendingRows.length) {
        message.warning('勾选的开票信息中没有待审批的记录');
        return;
      }
      Modal.confirm({
        title: '批量审核确认',
        content: `确定批量审核通过 ${pendingRows.length} 条待审批开票信息吗？`,
        onOk: async () => {
          try {
            await batchApproveInvoiceInfo(pendingRows.map((r: DeliInvoiceInfo) => r.id));
            message.success('批量审核完成');
            fetchData();
          } catch (e: any) {
            message.error(e?.message || '批量审核失败');
            fetchData();
          }
        }
      });
    };

    /** 单条删除 */
    const handleDelete = (record: DeliInvoiceInfo) => {
      Modal.confirm({
        title: '删除确认',
        content: `确定删除开票信息 ${record.code} 吗？已被开票单引用的记录无法删除。`,
        onOk: async () => {
          try {
            await deleteInvoiceInfo(record.id);
            message.success('删除成功');
            fetchData();
          } catch (e: any) {
            message.error(e?.message || '删除失败');
          }
        }
      });
    };

    /** 批量删除 */
    const handleBatchDelete = () => {
      const rows = getCheckedRows();
      if (!rows.length) {
        message.warning('请先勾选需要删除的开票信息');
        return;
      }
      Modal.confirm({
        title: '批量删除确认',
        content: `确定删除勾选的 ${rows.length} 条开票信息吗？已被开票单引用的记录无法删除。`,
        onOk: async () => {
          try {
            await batchDeleteInvoiceInfo(rows.map((r: DeliInvoiceInfo) => r.id));
            message.success('批量删除完成');
            fetchData();
          } catch (e: any) {
            message.error(e?.message || '批量删除失败');
            fetchData();
          }
        }
      });
    };

    /** 批量导出：按勾选行发起异步导出任务，文件生成后在导出任务中心下载 */
    const handleBatchExport = async () => {
      const rows = getCheckedRows();
      if (!rows.length) {
        message.warning('请先勾选需要导出的开票信息');
        return;
      }
      exportLoading.value = true;
      try {
        await batchExportInvoiceInfo(rows.map((r: DeliInvoiceInfo) => r.id));
        message.success('导出任务已创建，请稍后在导出任务中心下载');
      } catch (e: any) {
        message.error(e?.message || '导出任务创建失败');
      } finally {
        exportLoading.value = false;
      }
    };

    /** 判断记录是否已生效（已审批通过或直接生效） */
    const isEffective = (record: DeliInvoiceInfo) =>
      record.approvalStatus === 'APPROVED' || record.approvalStatus === 'DIRECT_EFFECTIVE';

    /** 品牌工作台不可编辑已审批通过的记录；运营中心不限制 */
    const canEdit = (record: DeliInvoiceInfo) => {
      if (isSeller.value && record.approvalStatus === 'APPROVED') return false;
      return true;
    };

    const handleImport = () => {
      importVisible.value = true;
    };

    /** FileUpload @change 回调：从受管上传返回的文件数组取最后一个文件的 url，若数组为空则清空 */
    const handleLicenseChange = (files: any[]) => {
      const last = files && files.length ? files[files.length - 1] : null;
      formState.businessLicenseFile = last?.url || '';
    };

    /** 移除营业执照文件 */
    const handleLicenseRemove = () => {
      formState.businessLicenseFile = '';
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      submitLoading,
      exportLoading,
      tableData,
      searchForm,
      formState,
      pagination,
      showAddForm,
      editingRecord,
      licenseFileValue,
      tableRef,
      rejectVisible,
      rejectLoading,
      rejectReason,
      rejectingRecord,
      importVisible,
      approvalStatusTextMap,
      approvalStatusColorMap,
      isDistributor,
      isSeller,
      isOperationCenter,
      isEffective,
      canEdit,
      fetchData,
      handleSearch,
      handleReset,
      handlePaginationChange,
      handleAdd,
      handleEdit,
      handleCancel,
      handleRejectCancel,
      handleSubmit,
      handleApprove,
      handleReject,
      handleRejectConfirm,
      handleBatchApprove,
      handleBatchExport,
      handleDelete,
      handleBatchDelete,
      handleImport,
      handleLicenseChange,
      handleLicenseRemove
    };
  }
});
